import React, { useEffect, useState, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import cx from 'classnames';
import Image from 'next/image';
import styles from './accordion.module.scss';
import AccordionConfig from '../../../models/accordion-config';
import CSSFilter from '../../../helpers/css-filter';

type Props = {
  title: string;
  children: any;
  config: AccordionConfig;
  customClass?: string;
  includeHR?: boolean;
  shouldOpen?: boolean;
};

const Accordion = ({
  title,
  children,
  customClass,
  config,
  shouldOpen = false,
  includeHR = false
}: Props) => {
  const {
    closedColor,
    openedColor,
    icon,
    openedBackgroundColor,
    analyticsAttributes
  } = config;
  const [open, setOpen] = useState(shouldOpen);
  const [contentMaxHeight, setContentMaxHeight] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState(CSSFilter(closedColor));

  const calcContentMaxHeight = () => {
    if (ref && ref.current) {
      setContentMaxHeight(ref.current.scrollHeight);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', calcContentMaxHeight);
    return () => window.removeEventListener('resize', calcContentMaxHeight);
  }, []);

  useEffect(() => {
    setFilter(CSSFilter(open ? openedColor : closedColor));
  }, [open]);

  useEffect(() => {
    calcContentMaxHeight();
  }, [ref]);

  const { scaleY, buttonColor, contentColor, ...props } = useSpring({
    scaleY: open ? -1 : 1,
    buttonColor: open
      ? openedBackgroundColor || 'rgba(0, 0, 0, 0)'
      : 'rgba(0, 0, 0, 0)',
    contentColor: open ? openedColor : closedColor,
    opacity: open ? 1 : 0,
    maxHeight: open ? `${contentMaxHeight}px` : '0px',
    config: { duration: 300 }
  });

  return (
    <div>
      <animated.button
        className={cx(styles.accordion__heading, customClass, {
          [styles['accordion__heading--noborder']]: includeHR
        })}
        onClick={() => setOpen(!open)}
        type="button"
        style={{
          backgroundColor: buttonColor.to((color) => color),
          color: contentColor?.to((color) => color)
        }}
        {...(open ? analyticsAttributes?.opened : analyticsAttributes?.closed)}
      >
        <div>{title}</div>
        <animated.div
          className={styles.accordion__chevron}
          style={{
            transform: scaleY.to((scale) => `scaleY(${scale})`),
            ...filter
          }}
        >
          {icon && (
            <Image
              alt={icon.alt}
              src={icon.src}
              height={icon.height}
              width={icon.width}
              layout="fixed"
            />
          )}
        </animated.div>
      </animated.button>
      <animated.div
        ref={ref}
        style={{
          overflow: 'hidden',
          ...props
        }}
      >
        {children}
      </animated.div>
      {includeHR && (
        <hr
          className={styles.accordion__hr}
          style={{
            borderTop: `.5px solid ${open ? openedColor : closedColor}`
          }}
        />
      )}
    </div>
  );
};

export default Accordion;
